import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate, formatViews } from "@/lib/articles";
import type { Article } from "@/mocks/articles";

interface HeroCarouselProps {
  articles: Article[];
}

export default function HeroCarousel({ articles }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const total = articles.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const active = articles[index];

  return (
    <section
      className="group relative h-[420px] w-full overflow-hidden rounded-lg md:h-[540px]"
      aria-roledescription="轮播"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          go(delta < 0 ? index + 1 : index - 1);
        }
        touchStartX.current = null;
      }}
    >
      {articles.map((article, i) => (
        <div
          key={article.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 蜜桃33`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-foreground-950/10" />
        </div>
      ))}

      <div className="relative flex h-full w-full flex-col justify-end p-6 md:p-10 lg:p-12">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/category/${active.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {active.categoryName}
            </Link>
            <span className="flex items-center gap-1.5 text-xs text-background-200">
              <i className="ri-calendar-line" />
              {formatDate(active.publishedAt)}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-background-200">
              <i className="ri-eye-line" />
              {formatViews(active.views)}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 text-balance md:text-4xl lg:text-[42px]">
            {active.title}
          </h1>
          <p className="mt-3 line-clamp-2 max-w-2xl text-sm leading-relaxed text-background-200/90 md:text-base">
            {active.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/category/news"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览更多资讯
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:left-5 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:right-5 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 right-6 flex items-center gap-2 md:bottom-10 md:right-10">
        {articles.map((article, i) => (
          <button
            key={article.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-7 bg-primary-400" : "w-2.5 bg-background-50/60 hover:bg-background-50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}